import { type Component, createEffect, createMemo, createResource, createSignal, For, onCleanup, Show } from 'solid-js'
import Alert from '~/components/Alert'
import Button from '~/components/Button'
import ChatActivity from '~/components/ChatActivity'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Markdown from '~/components/Markdown'
import VoiceCallPanel from '~/layers/VoiceCallPanel'
import { aiService, type PublicChatConfigType } from '~/services/ai.service'
import type { ActivityType } from '~/utils/chat'
import { resolveError } from '~/utils/error'

interface BubbleType {
  role: 'user' | 'assistant'
  content: string
}

const MAX_CHARS = 2000

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api'

/** WS de voz PÚBLICO: sin token; el backend resuelve empresa y agente por dominio. */
function publicVoiceWsUrl() {
  return `${API_URL.replace(/^http/, 'ws')}/ai/public/voice`
}

/**
 * Asesor comercial para visitantes de la web. Sin sesión: el backend resuelve la
 * empresa por el dominio y aplica su propio límite por IP.
 *
 * Si la empresa no tiene asesor configurado, /ai/public/config devuelve
 * enabled:false y el widget no se pinta: nada que mantener en la landing.
 */
const AsesorWidget: Component = () => {
  // Si la API no responde, el widget simplemente no aparece: es un extra de la
  // landing, no debe romperla.
  const [config] = createResource<PublicChatConfigType>(() => aiService.publicConfig().catch(() => ({ enabled: false })))

  const [open, setOpen] = createSignal(false)
  const [mode, setMode] = createSignal<'chat' | 'call'>('chat')
  const [messages, setMessages] = createSignal<BubbleType[]>([])
  const [input, setInput] = createSignal('')
  const [sending, setSending] = createSignal(false)
  const [activity, setActivity] = createSignal<ActivityType>({ state: 'idle' })
  const [error, setError] = createSignal('')

  // Si sólo hay voz (sin chat de texto), el widget abre directamente en llamada.
  createEffect(() => {
    const c = config()
    if (c?.enabled && c.chat === false && c.voice) setMode('call')
  })

  const sendDisabled = createMemo(() => !sending() && !input().trim())

  let controller: AbortController | undefined
  let scrollBox: HTMLDivElement | undefined

  onCleanup(() => controller?.abort())

  const scrollToBottom = () =>
    requestAnimationFrame(() => {
      if (scrollBox) scrollBox.scrollTop = scrollBox.scrollHeight
    })

  const send = async () => {
    const text = input().trim()
    if (!text || sending()) return

    setError('')
    const history = [...messages().map(m => ({ role: m.role, content: m.content })), { role: 'user' as const, content: text }]

    setMessages(prev => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }])
    setInput('')
    setSending(true)
    setActivity({ state: 'thinking' })
    scrollToBottom()

    controller = new AbortController()

    const patchLast = (chunk: string) => {
      setMessages(prev => {
        const next = [...prev]
        next[next.length - 1] = { role: 'assistant', content: next[next.length - 1].content + chunk }
        return next
      })
      scrollToBottom()
    }

    try {
      await aiService.publicChatStream(
        history,
        event => {
          if (event.kind === 'content') {
            setActivity({ state: 'idle' })
            patchLast(event.text)
          } else if (event.kind === 'tool_call') {
            setActivity({ state: 'tool', name: event.tool })
          } else if (event.kind === 'tool_result') {
            setActivity({ state: 'thinking' })
          }
        },
        { signal: controller.signal }
      )
    } catch (err: unknown) {
      if ((err as Error)?.name !== 'AbortError') {
        setMessages(prev => (prev[prev.length - 1]?.content === '' ? prev.slice(0, -1) : prev))
        setError(resolveError(err))
      }
    } finally {
      setSending(false)
      setActivity({ state: 'idle' })
      controller = undefined
    }
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void send()
    }
  }

  return (
    <Show when={config()?.enabled}>
      {/* Botones flotantes: chat y/o llamada, según lo que ofrezca el sitio. */}
      <Show when={!open()}>
        <div class="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2">
          <Show when={config()?.voice}>
            <Button
              onClick={() => {
                setMode('call')
                setOpen(true)
              }}
              aria-label="Llamar al asesor"
              prependIcon="material-symbols-light:phone-in-talk"
              pill
              size="lg"
              class="shadow-lg shadow-primary-600/30"
            >
              Llamar
            </Button>
          </Show>
          <Show when={config()?.chat !== false}>
            <Button
              onClick={() => {
                setMode('chat')
                setOpen(true)
              }}
              aria-label="Abrir el chat"
              prependIcon="material-symbols-light:chat-outline"
              pill
              size="lg"
              class="shadow-lg shadow-primary-600/30"
            >
              ¿Hablamos?
            </Button>
          </Show>
        </div>
      </Show>

      {/* Panel */}
      <Show when={open()}>
        <div class="fixed inset-x-3 bottom-3 z-50 flex h-[70dvh] max-h-[560px] flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl sm:inset-x-auto sm:right-5 sm:w-96">
          <header class="flex items-center justify-between border-b border-border bg-surface px-4 py-3">
            <div class="flex items-center gap-2 min-w-0">
              <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white">
                <Show when={config()?.avatar} fallback={<Icon icon="material-symbols-light:record-voice-over-outline" size={18} />}>
                  <span class="text-base">{config()?.avatar}</span>
                </Show>
              </span>
              <div class="min-w-0">
                <p class="truncate text-sm font-semibold text-foreground">{config()?.name ?? 'Asesor'}</p>
                <p class="truncate text-xs text-foreground-subtle">Normalmente responde al instante</p>
              </div>
            </div>
            <div class="flex items-center gap-1">
              {/* Alternar chat / llamada, sólo si el sitio tiene ambos. */}
              <Show when={config()?.voice && config()?.chat}>
                <Show
                  when={mode() === 'chat'}
                  fallback={<Button variant="text" color="default" icon="material-symbols-light:chat-outline" iconSize={20} onClick={() => setMode('chat')} aria-label="Volver al chat" title="Chat" />}
                >
                  <Button variant="text" color="default" icon="material-symbols-light:phone-in-talk" iconSize={20} onClick={() => setMode('call')} aria-label="Llamar" title="Llamar" />
                </Show>
              </Show>
              <Button variant="text" color="default" icon="material-symbols-light:close" iconSize={20} onClick={() => setOpen(false)} aria-label="Cerrar el asesor" />
            </div>
          </header>

          {/* Modo llamada: en vez del chat de texto, la interfaz de voz pública. */}
          <Show when={mode() === 'call'}>
            <VoiceCallPanel url={publicVoiceWsUrl()} name={config()?.name ?? 'Asesor'} avatar={config()?.avatar} compact />
          </Show>

          <Show when={mode() === 'chat'}>
            <div ref={scrollBox} class="flex-1 space-y-3 overflow-y-auto p-4">
              <Show when={messages().length === 0}>
                <div class="rounded-2xl rounded-bl-sm bg-surface-muted px-4 py-2 text-sm text-foreground">{config()?.greeting ?? '¿En qué puedo ayudarte?'}</div>
              </Show>

              <For each={messages()}>
                {(msg, i) => (
                  <div class={msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                    <div
                      class={
                        msg.role === 'user'
                          ? 'max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-sm bg-primary-600 px-4 py-2 text-sm text-white'
                          : 'max-w-[85%] min-w-0 break-words rounded-2xl rounded-bl-sm bg-surface-muted px-4 py-2 text-sm text-foreground'
                      }
                    >
                      <Show
                        when={msg.content}
                        fallback={
                          <Show when={i() === messages().length - 1 && activity().state !== 'idle'}>
                            <ChatActivity activity={activity()} />
                          </Show>
                        }
                      >
                        {/* Lo que escribe el visitante se muestra tal cual: es su texto. */}
                        <Show when={msg.role === 'assistant'} fallback={msg.content}>
                          <Markdown content={msg.content} />
                        </Show>
                      </Show>
                    </div>
                  </div>
                )}
              </For>

              <Show when={activity().state !== 'idle' && messages()[messages().length - 1]?.content}>
                <ChatActivity activity={activity()} />
              </Show>

              <Show when={error()}>
                <Alert variant="danger">{error()}</Alert>
              </Show>
            </div>

            <div class="border-t border-border p-3">
              <Input
                class="w-full !rounded-full !pe-14"
                placeholder="Escribe tu mensaje…"
                maxLength={MAX_CHARS}
                value={input()}
                onInput={e => setInput(e.currentTarget.value)}
                onKeyDown={onKeyDown}
                disabled={sending()}
                appendIcon={
                  <Button
                    icon={sending() ? 'material-symbols-light:stop-outline' : 'material-symbols-light:arrow-upward'}
                    color={sending() ? 'danger' : 'primary'}
                    iconSize={18}
                    size="sm"
                    pill
                    disabled={sendDisabled()}
                    aria-label={sending() ? 'Detener' : 'Enviar'}
                    onClick={() => (sending() ? controller?.abort() : void send())}
                  />
                }
              />
            </div>
          </Show>
        </div>
      </Show>
    </Show>
  )
}

export default AsesorWidget
